// Tab "Lịch sử cập nhật": sửa thông tin khách (trước → sau), phân loại, liên kết, thẻ liệu trình, chữ ký, ảnh, lần đến + thông báo từ API
import { useEffect, useMemo, useState } from 'react';
import { Avatar, Empty, Input, Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import { ArrowRight, Search } from 'lucide-react';
import { getNotificationReadLogs } from '../../api/NotificationReadLog';
import { AUDIT_KIND, type AuditKind, type AuditLog } from './data';
import { derivedHistory } from './logic';
import useC360Store from './store';
import type { C360 } from './useCustomer360';

const initials = (name?: string) => {
  const parts = (name || 'Hệ thống').trim().split(/\s+/).filter(Boolean);
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();
const PAGE = 30;

export default function HistoryPanel({ c, customerId }: { c: C360; customerId?: number }) {
  const stored = useC360Store(s => s.auditLogs[c.key]);
  const [api, setApi] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState<'all' | AuditKind>('all');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);

  // Thông báo đã đọc (API cũ) vẫn hiển thị trong cùng dòng thời gian
  useEffect(() => {
    if (!customerId) return;
    let alive = true;
    setLoading(true);
    getNotificationReadLogs({ customer_id: customerId, limit: 50 }).then(res => {
      if (!alive) return;
      setApi(res.status && res.data ? res.data.items.map(i => ({
        id: `api-${i.id}`, at: i.created_at, by: i.notification?.creator?.fullname || 'Hệ thống', kind: 'notify' as const,
        title: i.notification?.title || 'Thông báo', note: i.notification?.content || undefined,
      })) : []);
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [customerId]);

  const all = useMemo(() => [...(stored || []), ...derivedHistory(c.data, c.visits), ...api].sort((a, b) => b.at.localeCompare(a.at)), [stored, c.data, c.visits, api]);
  const counts = useMemo(() => all.reduce<Record<string, number>>((m, x) => ({ ...m, [x.kind]: (m[x.kind] || 0) + 1 }), {}), [all]);
  const nq = normalize(q.trim());
  const list = all.filter(x => (kind === 'all' || x.kind === kind)
    && (!nq || normalize(`${x.title} ${x.note || ''} ${x.by} ${(x.changes || []).map(ch => `${ch.label} ${ch.from} ${ch.to}`).join(' ')}`).includes(nq)));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented value={kind} onChange={v => { setKind(v as typeof kind); setLimit(PAGE); }}
          options={[{ value: 'all', label: `Tất cả (${all.length})` }, ...(Object.keys(AUDIT_KIND) as AuditKind[]).filter(k => counts[k]).map(k => ({ value: k, label: `${AUDIT_KIND[k].label} (${counts[k]})` }))]} />
        <Input allowClear className="!w-56" prefix={<Search className="h-4 w-4 text-slate-400" />} placeholder="Tìm nội dung, người sửa" value={q} onChange={e => { setQ(e.target.value); setLimit(PAGE); }} />
        {loading && <Spin size="small" />}
      </div>

      {!list.length ? <Empty className="py-8" description="Chưa có lịch sử cập nhật" /> : (
        <div className="relative pl-4">
          <div className="absolute bottom-0 left-4 top-0 w-[2px] bg-gray-200" />
          {list.slice(0, limit).map(x => {
            const meta = AUDIT_KIND[x.kind];
            return (
              <div key={x.id} className="relative mb-4">
                <div className="absolute left-0 top-3 h-3 w-3 -translate-x-1/2 rounded-full border-4 bg-white" style={{ borderColor: meta.color }} />
                <div className="ml-4 rounded-lg bg-gray-50 p-3 dark:bg-[#1f1f1f]">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Avatar size={24} style={{ background: meta.color }}>{initials(x.by)}</Avatar>
                    <span className="text-sm font-medium text-000000e0">{x.by || 'Hệ thống'}</span>
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ background: meta.color }}>{meta.label}</span>
                    <span className="ml-auto text-xs text-gray-400">{dayjs(x.at).format('DD/MM/YYYY HH:mm')}</span>
                  </div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{x.title}</div>
                  {x.changes?.length ? (
                    <div className="mt-1.5 divide-y divide-slate-100 rounded-md border border-slate-200 bg-white text-xs">
                      {x.changes.map((ch, i) => (
                        <div key={i} className="grid grid-cols-[120px_1fr] gap-2 px-2.5 py-1.5 sm:grid-cols-[140px_1fr]">
                          <span className="text-slate-500">{ch.label}</span>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="text-slate-400 line-through">{ch.from || '—'}</span>
                            <ArrowRight className="h-3 w-3 text-slate-400" />
                            <b className="text-slate-800">{ch.to || '—'}</b>
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {x.note && <div className="mt-1 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">{x.note}</div>}
                </div>
              </div>
            );
          })}
          {list.length > limit && (
            <button onClick={() => setLimit(l => l + PAGE)} className="ml-4 text-sm font-semibold text-[#2c6b7f] hover:underline">Xem thêm ({list.length - limit})</button>
          )}
        </div>
      )}
    </div>
  );
}
